import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../lib/supabase'
import type { Book, FieldVisibility, Session, Profile } from '@/types'

/**
 * Notification state
 */
interface Notification {
    isOpen: boolean
    title: string
    message: string
    type: 'info' | 'success' | 'danger' | 'warning'
}

/**
 * Extended book with profile information for Biblioverso
 */
interface BookWithProfile extends Book {
    profiles?: {
        full_name: string
    }
}

/**
 * Parameters for useBookList hook
 */
interface UseBookListParams {
    sharedUserId?: string
    isBiblioverso: boolean
    session: Session | null
}

/**
 * Return type for useBookList hook
 */
interface UseBookListReturn {
    // State
    books: BookWithProfile[]
    loading: boolean
    notification: Notification
    setNotification: (notification: Notification) => void

    // Unique values for autocomplete/filters
    existingAuthors: string[]
    existingPublishers: string[]
    existingLocations: string[]
    existingSeries: string[]
    existingCategories: string[]
    existingLanguages: string[]

    // Shared settings
    sharedSettings: string[] | null
    sharedProfile: Profile | null
    biblioversoSettings: Record<string, string[]>

    // Functions
    loadBooks: () => Promise<void>
    deleteBook: (bookId: string) => Promise<boolean>
    updateUniqueValues: (booksData: BookWithProfile[]) => void
    showNotify: (title: string, message: string, type?: Notification['type']) => void
    getUniqueBiblionautas: () => string[]
    getVisibleFields: (book: BookWithProfile, cardVisibleFields: FieldVisibility) => FieldVisibility

    // Computed
    currentUserId: string | undefined
}

/**
 * Custom hook for managing book data and CRUD operations
 * 
 * Main coordinator hook for BookList - handles data loading, CRUD, and shared library logic
 */
export function useBookList({ sharedUserId, isBiblioverso, session }: UseBookListParams): UseBookListReturn {
    const currentUserId = session?.user?.id

    // State
    const [books, setBooks] = useState<BookWithProfile[]>([])
    const [loading, setLoading] = useState(true)
    const [notification, setNotification] = useState<Notification>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
    })

    // Unique values for autocomplete/filters
    const [existingAuthors, setExistingAuthors] = useState<string[]>([])
    const [existingPublishers, setExistingPublishers] = useState<string[]>([])
    const [existingLocations, setExistingLocations] = useState<string[]>([])
    const [existingSeries, setExistingSeries] = useState<string[]>([])
    const [existingCategories, setExistingCategories] = useState<string[]>([])
    const [existingLanguages, setExistingLanguages] = useState<string[]>([])

    // Shared library settings
    const [sharedSettings, setSharedSettings] = useState<string[] | null>(null)
    const [sharedProfile, setSharedProfile] = useState<Profile | null>(null)
    const [biblioversoSettings, setBiblioversoSettings] = useState<Record<string, string[]>>({})

    // Notification helper
    const showNotify = useCallback((title: string, message: string, type: Notification['type'] = 'info') => {
        setNotification({ isOpen: true, title, message, type })
    }, [])

    // Load shared library settings
    const loadSharedSettings = useCallback(async () => {
        if (!sharedUserId) return

        // 1. Fetch settings (visible fields)
        const { data } = await supabase
            .from('shared_library_settings')
            .select('visible_fields')
            .eq('user_id', sharedUserId)
            .single()

        if (data) {
            setSharedSettings(data.visible_fields as string[])
        }

        // 2. Fetch profile for header
        const { data: profile } = await supabase.from('profiles').select('full_name, email').eq('id', sharedUserId).single()

        if (profile) {
            setSharedProfile(profile as Profile)
        }
    }, [sharedUserId])

    // Load biblioverso settings for multiple users
    const loadBiblioversoSettings = useCallback(async (booksData: BookWithProfile[]) => {
        const uniqueUserIds = [...new Set(booksData.map((b) => b.user_id))].filter(
            (id): id is string => id !== undefined
        )

        if (uniqueUserIds.length === 0) return

        const { data } = await supabase
            .from('shared_library_settings')
            .select('user_id, visible_fields')
            .in('user_id', uniqueUserIds)

        if (data) {
            const settingsMap: Record<string, string[]> = {}
            data.forEach((row) => {
                settingsMap[row.user_id] = row.visible_fields as string[]
            })
            setBiblioversoSettings(settingsMap)
        }
    }, [])

    // Update unique values for autocomplete
    const updateUniqueValues = useCallback((booksData: BookWithProfile[]) => {
        const authors = [...new Set(booksData.flatMap((b) => b.author || []))].filter(Boolean).sort((a, b) => a.localeCompare(b))
        const publishers = [...new Set(booksData.map((b) => b.publisher).filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b))
        const locations = [...new Set(booksData.map((b) => b.location).filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b))
        const series_list = [...new Set(booksData.map((b) => b.series).filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b))
        const categories = [...new Set(booksData.flatMap((b) => b.category || []))].filter(Boolean).sort((a, b) => a.localeCompare(b))
        const languages = [...new Set(booksData.map((b) => b.language).filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b))

        setExistingAuthors(authors)
        setExistingPublishers(publishers)
        setExistingLocations(locations)
        setExistingSeries(series_list)
        setExistingCategories(categories)
        setExistingLanguages(languages)
    }, [])

    // Load books from database
    const loadBooks = useCallback(async () => {
        let query = supabase.from('books').select('*').order('created_at', { ascending: false })

        if (sharedUserId) {
            query = query.eq('user_id', sharedUserId)
        } else if (isBiblioverso) {
            // Fetch all accessible books (RLS allows my books + shared books)
            // Also fetch profile name for "Biblionauta" column
            query = supabase.from('books').select('*, profiles(full_name)').order('created_at', { ascending: false })
        } else {
            // Regular "My Library" mode
            const {
                data: { user },
            } = await supabase.auth.getUser()
            if (user) query = query.eq('user_id', user.id)
        }

        const { data, error } = await query

        if (!error && data) {
            setBooks(data as BookWithProfile[])
            updateUniqueValues(data as BookWithProfile[])
            if (isBiblioverso) {
                loadBiblioversoSettings(data as BookWithProfile[])
            }
        }
        setLoading(false)
    }, [sharedUserId, isBiblioverso, loadBiblioversoSettings, updateUniqueValues])

    // Delete a book
    const deleteBook = useCallback(
        async (bookId: string): Promise<boolean> => {
            const { error } = await supabase.from('books').delete().eq('id', bookId)

            if (!error) {
                const newBooks = books.filter((book) => book.id !== bookId)
                setBooks(newBooks)
                updateUniqueValues(newBooks)
                return true
            }
            return false
        },
        [books, updateUniqueValues]
    )

    // Get unique biblionautas (for Biblioverso filter)
    const getUniqueBiblionautas = useCallback(() => {
        const names = books.map((b) => b.profiles?.full_name || 'Mi Biblioteca').filter(Boolean)
        return [...new Set(names)].sort((a, b) => a.localeCompare(b))
    }, [books])

    // Helper to determine field visibility for a specific book (Biblioverso/Shared logic)
    const getVisibleFields = useCallback(
        (book: BookWithProfile, cardVisibleFields: FieldVisibility): FieldVisibility => {
            const fields = { ...cardVisibleFields }
            const isMyBook = book.user_id === currentUserId
            let ownerSettings: string[] | null = null

            if (isBiblioverso) {
                ownerSettings = biblioversoSettings && book.user_id ? biblioversoSettings[book.user_id] : null
            } else if (sharedUserId) {
                ownerSettings = sharedSettings
            }

            // If it's NOT my book and we are in a shared context, apply owner permissions
            if (!isMyBook && ownerSettings) {

                Object.keys(fields).forEach((uiKey) => {
                    if (uiKey === 'author') return // Always share permission for Author

                    // Location, Notes and Date are NEVER shared for other users' books
                    if (uiKey === 'location' || uiKey === 'notes' || uiKey === 'created_at') {
                        fields[uiKey as keyof FieldVisibility] = false
                        return
                    }

                    // For all other fields: Visibility = Local Preference AND Owner Permission
                    // We assume ownerSettings contains English keys now
                    fields[uiKey as keyof FieldVisibility] = fields[uiKey as keyof FieldVisibility] && ownerSettings.includes(uiKey)
                })
            } else if (!isMyBook && (isBiblioverso || sharedUserId)) {
                // Shared context, not my book, and no settings loaded: show minimal info
                Object.keys(fields).forEach((uiKey) => {
                    if (uiKey !== 'author') {
                        fields[uiKey as keyof FieldVisibility] = false
                    }
                })
            }

            return fields
        },
        [currentUserId, isBiblioverso, biblioversoSettings, sharedUserId, sharedSettings]
    )

    // Load books on mount and when dependencies change
    useEffect(() => {
        const initialize = async () => {
            await loadBooks()
            if (sharedUserId) {
                await loadSharedSettings()
            }
        }
        initialize()
    }, [sharedUserId, loadBooks, loadSharedSettings])

    return {
        // State
        books,
        loading,
        notification,
        setNotification,

        // Unique values
        existingAuthors,
        existingPublishers,
        existingLocations,
        existingSeries,
        existingCategories,
        existingLanguages,

        // Shared settings
        sharedSettings,
        sharedProfile,
        biblioversoSettings,

        // Functions
        loadBooks,
        deleteBook,
        updateUniqueValues,
        showNotify,
        getUniqueBiblionautas,
        getVisibleFields,

        // Computed
        currentUserId,
    }
}
